import { ComponentProps, useId, useImperativeHandle, useRef } from 'react'
import { OverrideProperties } from 'type-fest'

import { SizeOptions } from '../../../types/types'
import { astraTwMerge } from '../../Utilities/astraTwMerge'
import { AstraInputWrapper, getInputDescribedBy, InputBase, inputTypographyStyles, textInputFrameCursorStyles } from '../AstraInputWrapper/AstraInputWrapper'
import { useAstraPrivacyMode } from '../AstraPrivacyMode/AstraPrivacyMode'

export const textFieldTypes = ['date', 'datetime-local', 'email', 'month', 'number', 'password', 'search', 'tel', 'text', 'time', 'url', 'week'] as const

type TextFieldTypes = (typeof textFieldTypes)[number]

// Free-text input types whose content should be hidden in privacy mode. Date/time
// types and `password` are left untouched (native pickers / already hidden).
const privacyMaskableTypes = new Set<TextFieldTypes>(['email', 'number', 'search', 'tel', 'text', 'url'])

type Props = InputBase &
    OverrideProperties<
        ComponentProps<'input'>,
        {
            size?: SizeOptions
            type?: TextFieldTypes
        }
    >

/**
 * Primary UI component for user interaction
 *
 * This component is a text input field that allows users to input text.
 */
export const AstraTextField = ({
    ref,
    startIcon,
    endIcon,
    size = 'medium',
    className,
    label,
    placeholder,
    error,
    helperText,
    errorText,
    disabled,
    readOnly,
    fullWidth,
    onChange,
    type = 'text',
    id,
    name,
    ...props
}: Props) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)
    const generatedId = useId()
    const inputId = id ?? generatedId
    const isPrivacyMode = useAstraPrivacyMode()
    const resolvedType = isPrivacyMode && privacyMaskableTypes.has(type) ? 'password' : type

    return (
        <AstraInputWrapper
            id={inputId}
            label={label}
            labelHtmlFor={inputId}
            helperText={helperText}
            errorText={errorText}
            error={error}
            disabled={disabled}
            readOnly={readOnly}
            fullWidth={fullWidth}
            className={astraTwMerge(textInputFrameCursorStyles(disabled), className)}
            size={size}
            startIcon={startIcon}
            endIcon={endIcon}
            onClick={() => inputRef.current?.focus()}
        >
            <input
                {...props}
                aria-describedby={getInputDescribedBy({ id: inputId, helperText, errorText, error })}
                aria-invalid={error || undefined}
                onChange={onChange}
                readOnly={readOnly}
                disabled={disabled}
                placeholder={placeholder}
                name={name}
                type={resolvedType}
                aria-label={label}
                className={astraTwMerge(
                    'h-full grow bg-inherit placeholder:text-subtle focus:outline-none active:outline-none disabled:placeholder:text-disabled',
                    error && !disabled && 'text-critical',
                    inputTypographyStyles[size],
                    fullWidth ? 'w-full' : ''
                )}
                ref={inputRef}
                id={inputId}
            />
        </AstraInputWrapper>
    )
}
